/// <reference types="macplugin" />
import Object from 'apmui/util/Object';
import { divLog } from 'apmui/util/divLog';
import { IComponentPackagesMac, IComponentPackageMac } from './Model';
import setImmediate from 'apmui/util/setImmediate';

enum EVENT {
    // Direct actions
    INIT = 'init',
    CHANGE_TEXT = 'changeText',
    CHANGE_PROGRESS = 'changeProgress',
    CHANGE_URL = 'changeUrl',

    // Calling functions
    INSPECTION_HOST_MAN_INSTALL = 'inspectionHostManualInstall',
    INSPECTION_HOST_UPDATE = 'inspectionHostUpdate',
    INSPECTION_HOST_UPDATE_CB = 'installerControlUpdateCB',
    INSPECTION_HOST_RUN = 'inspectionHostRun',
}

enum VERSION {
    RETAIN = 2,
    UPDATE = 1,
    INSTALL = 0,
}

/* event: EVENT */
type ICallback = (event: EVENT, status: string, arg: string) => void;
let callback: ICallback;

let inspectionHost: IComponentPackageMac;
let container: HTMLElement;
let session = '';
let baseUrl = '';
function installMacPlugin(componentPackages: IComponentPackagesMac, sess: string, url: string, cb: ICallback) {
    divLog('installMacPlugin - enter');

    inspectionHost = componentPackages.inspectionHost;
    session = sess;
    baseUrl = url;
    callback = cb;

    if (!container) {
        container = document.body.appendChild(document.createElement('div'));
        // container.hidden = true;
        container.id = 'apmui-plugin-container';
    }

    callback(EVENT.INIT, '', '');
    switch (checkVersion()) {
        case VERSION.RETAIN:
            setImmediate(() => runPlugin());
            break;
        case VERSION.UPDATE:
            setImmediate(() => updatePlugin());
            break;
        case VERSION.INSTALL:
            manualInstallationScreen();
            break;
    }

    divLog('installMacPlugin - exit');
}

const VERSION_ERROR = '0.0.0.0';
const VERSION_UNKNOWN = '0.0.0.1';
/**
 * Checks version of given component configuration
 * @param component component configuration to test
 * @returns VERSION_ERROR if component is not registered or wrong,
 * VERSION_UNKNOWN if registered but no version retrieved, X.X.X.X if correct
 */
function getPackageVersion(component: IComponentPackageMac) {
    divLog(`getVersion - enter mimeType=${component.mimeType}`);

    let version = VERSION_ERROR;
    const mimeType = navigator.mimeTypes[component.mimeType];
    if (!mimeType) {
        divLog('mimeType is not registered');
    } else if (!mimeType.enabledPlugin) {
        divLog('mimeType is not enabled plugin');
    } else if (mimeType.enabledPlugin.name.indexOf(component.id) !== 0) {
        divLog('mimeType has another enabled plugin' + mimeType.enabledPlugin.name);
    } else {
        // check for the version our controls should have version inside description field
        const VER = 'ver. ';
        version = mimeType.enabledPlugin.description.toLowerCase();
        if (version.indexOf(VER) >= 0) {
            version = version.substring(version.indexOf(VER) + VER.length);
        }

        // plugin for Mac currently does not have a version number inside it's description field CR 92235.
        // put bogus minimal version "0.0.0.1" if we can not detect the version
        if (version.split('.').length !== 4) {
            version = VERSION_UNKNOWN;
        }
    }

    divLog(`getVersion - exit version=${version}`);

    return version;
}

/**
 * checks version of inspectionHost
 * @returns true if it's up to date, false otherwize
 */
function checkVersion() {
    divLog('checkVersion - enter');

    const version = getPackageVersion(inspectionHost);
    divLog(`version=${version} inspectionHost.version=${inspectionHost.version}`);
    let ret = VERSION.INSTALL;

    if (version !== VERSION_ERROR) {
        const serverV = inspectionHost.version.split('.').map((x) => Number(x));
        const clientV = version.split('.').map((x) => Number(x));
        const retain = clientV.every((cv, idx) => cv >= serverV[idx]);
        ret = retain ? VERSION.RETAIN : VERSION.UPDATE;
    }

    divLog(`checkVersion - exit ${ret}`);

    return ret;
}
/**
 * Displays manual installation screens,
 * Since we don't support automatic installation by now
 */
function manualInstallationScreen() {
    divLog('installationScreen - enter');

    callback(EVENT.INSPECTION_HOST_MAN_INSTALL, '', '');

    divLog('installationScreen - exit');
}

/**
 * Let's try updateing plugin
 */
function updatePluginCB(url: string, status: number) {
    divLog(`install - enter ${url} ${status}`);

    callback(EVENT.INSPECTION_HOST_UPDATE_CB, String(status), url);

    if (status === 0) {
        manualInstallationScreen();
    } else {
        runPlugin();
    }

    divLog('installCallback');
}

function updatePlugin() {
    divLog('updatePlugin - enter');

    callback(EVENT.INSPECTION_HOST_UPDATE, '', '');

    let res = false;
    try {
        const xpi = { [inspectionHost.id]: inspectionHost.url };
        res = InstallTrigger.install(xpi, updatePluginCB) > 0;
    } catch (e) {
        res = false;
    }

    if (!res) {
        manualInstallationScreen();
    }

    divLog('UpdatePlugin - exit');
}

/**
 * Version is ok, run plugin
 */
function runPlugin() {
    divLog('runPlugin() - enter');

    callback(EVENT.INSPECTION_HOST_RUN, '', '');

    const attributes = {
        type: inspectionHost.mimeType,
        version: '2.9',
        session,
        agenturl: baseUrl + `/my.policy_host?sess=${session}&dummy=0`,
        hostrooturl: baseUrl,
        last_param: 'dummy', // Portishead fans?
    };
    const attrStr = Object.entries(attributes)
        .map((x) => `${x[0]}="${x[1]}"`)
        .join(' ');

    container.innerHTML = '<embed ' + attrStr + ' >';

    divLog(`runPlugin() - exit ${container.innerHTML}`);
}

enum HOST_EVENT {
    SETTEXT = 'settext',
    FEXIT = 'fExit',
    FGOTOURL = 'fGoToURL',
    INSTALL_CONTROL_EVENT = 'install_control_event',
}

window[HOST_EVENT.SETTEXT] = (str: string) => {
    divLog(`${HOST_EVENT.SETTEXT} -- enter ${str}`);

    callback(EVENT.CHANGE_TEXT, str, '');

    divLog(`${HOST_EVENT.SETTEXT} -- exit`);
};

window[HOST_EVENT.FEXIT] = (url: string) => {
    divLog(`${HOST_EVENT.FEXIT} -- enter ${url}`);

    window[HOST_EVENT.FGOTOURL](url);

    divLog(`${HOST_EVENT.FEXIT} -- exit`);
};

window[HOST_EVENT.FGOTOURL] = (url: string, data: string) => {
    divLog(`${HOST_EVENT.FGOTOURL} -- enter ${url},` + data.substr(0, 10));

    if (url.indexOf('/redirect:') === 0) {
        url = unescape(url.substring(10, url.length));
    } else if (url.indexOf('/logon') === 0) {
        url = '/my.policy';
    } else if (url.indexOf('/logout') === 0) {
        url = '/my.logout.php3';
    } else if (url.indexOf('/policy/show_error.php3') >= 0) {
        url = url.replace('/policy/show_error.php3', '/my.logout.php3').replace('error_number=', 'errorcode=');
    }
    url = unescape(url);

    divLog(`url = ${url}`);

    callback(EVENT.CHANGE_URL, url, '');

    divLog(`${HOST_EVENT.FGOTOURL} -- exit`);
};

window[HOST_EVENT.INSTALL_CONTROL_EVENT] = (...args: string[]) => {
    divLog(`${HOST_EVENT.INSTALL_CONTROL_EVENT} -- enter + exit ` + args.join(' '));
};

export { installMacPlugin, EVENT };
